import { ButtonType, InlineAlertType } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Redirect,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import CaptiveDialog from 'features/rdc/components/CaptiveDialog/CaptiveDialog';
import useRDCNavigator from 'features/rdc/hooks/useRDCNavigator';
import { RDCSearchParams } from 'features/rdc/RDCSearchParams';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import { InlineAlert } from 'shared/components/vxl/InlineAlert/InlineAlert';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import theme from 'shared/styles/theme';

export default function CaptureBackPrompt() {
  const router = useRouter();
  const { t: translatedText } = useTranslation('rdc');
  const { session, account, amount, depositFlowId } =
    useLocalSearchParams<RDCSearchParams>();
  const { finish } = useRDCNavigator();
  const [modalVisible, setModalVisible] = useState(false);

  const { bottom } = useSafeAreaInsets();

  const handleBackButtonAction = () => {
    setModalVisible(true);
  };

  const logRdcCaptureExitedEvent = usePressableAnalytics({
    analyticsId: 'rdcCaptureExited',
  });

  useBackHandler(() => {
    handleBackButtonAction();
  });

  if (isWeb) {
    return <Redirect href="/move-money/check-deposit" />;
  }

  return (
    <View style={styles.mainContainer}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          gestureEnabled: false,
          title: translatedText('headerTitle'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              primaryOnPress={handleBackButtonAction}
            />
          ),
        }}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.topContainer}
        testID="capture-back-scroll-view"
      >
        <TitleLgText>{translatedText('backOfCheckPhoto.title')}</TitleLgText>
        <View style={styles.checklistStyle}>
          <BodyBaseText>
            {translatedText('backOfCheckPhoto.checklist.title')}
          </BodyBaseText>
          <BodyBaseText>
            {translatedText('backOfCheckPhoto.checklist.instructions.lineOne')}
          </BodyBaseText>
          <BodyBaseText>
            {translatedText('backOfCheckPhoto.checklist.instructions.lineTwo')}
          </BodyBaseText>
          <BodyBaseText>
            {translatedText(
              'backOfCheckPhoto.checklist.instructions.lineThree',
            )}
          </BodyBaseText>
        </View>
        <InlineAlert
          alertType={InlineAlertType.Warning}
          bodyText={translatedText('backOfCheckPhoto.warningAlertTitle')}
        />
      </ScrollView>
      <View
        style={[
          styles.footerContainer,
          { paddingBottom: bottom + theme.spacing.m },
        ]}
      >
        <Button
          label={translatedText('backOfCheckPhoto.buttons.primaryButton')}
          onPress={() => {
            router.replace({
              pathname: './capture',
              params: {
                session,
                account,
                amount,
                mode: 'back',
                depositFlowId,
              },
            });
          }}
        />
        <Button
          label={translatedText('backOfCheckPhoto.buttons.secondaryButton')}
          type={ButtonType.Secondary}
          onPress={() => setModalVisible(true)}
        />
        <CaptiveDialog
          isDialogVisible={modalVisible}
          setModalVisible={setModalVisible}
          handlePrimaryAction={() => {
            logRdcCaptureExitedEvent();
            finish();
          }}
          handleSecondaryAction={(value) => setModalVisible(value)}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  checklistStyle: {
    gap: theme.spacing.s,
  },
  footerContainer: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
  mainContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
  },
  scrollView: {
    flex: 1,
  },
  topContainer: {
    gap: theme.spacing.m,
    padding: theme.spacing.m,
  },
});
